/**
 * Lightweight structural validation for a Save. Not a full schema check — just
 * enough to reject obviously-corrupt or non-save values on load/import so the
 * caller can fall back to a fresh world instead of crashing. We intentionally do
 * NOT deep-validate every mon: a partially-broken save is better recovered than
 * thrown away, and the deeper layers tolerate missing optional state.
 */
import type { Save } from '@/shared';
import { SCHEMA_VERSION } from '@/shared';

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** True if `v` is plausibly a Save at the current schema version. */
export function isSaveLike(v: unknown): v is Save {
  if (!isObject(v)) return false;

  if (v.schemaVersion !== SCHEMA_VERSION) return false;

  if (!isObject(v.profile)) return false;
  const profile = v.profile;
  if (profile.id !== 'player') return false;
  if (typeof profile.slotId !== 'string' || profile.slotId.length === 0) return false;
  if (typeof profile.name !== 'string') return false;
  if (typeof profile.starterSpeciesId !== 'string') return false;

  if (!Array.isArray(v.team)) return false;
  if (!Array.isArray(v.boxes)) return false;

  if (!isObject(v.dex) || !Array.isArray(v.dex.seen) || !Array.isArray(v.dex.caught)) {
    return false;
  }
  if (!isObject(v.ranch)) return false;
  if (!isObject(v.society) || !Array.isArray(v.society.trainers)) return false;
  if (!isObject(v.lore) || !Array.isArray(v.lore.entries)) return false;
  // Quests (D17) are back-filled by the v5→v6 migration, so a validated save
  // normally carries the block. Tolerate its absence (a save mid-migration), but
  // reject a present-yet-malformed block so a corrupt world is recovered fresh.
  if (v.quests !== undefined) {
    if (
      !isObject(v.quests) ||
      !Array.isArray(v.quests.active) ||
      !Array.isArray(v.quests.completed) ||
      !Array.isArray(v.quests.badges)
    ) {
      return false;
    }
  }

  // Round-3 blocks (v7) are optional; tolerate absence, reject present-yet-malformed.
  if (v.care !== undefined && (!isObject(v.care) || !Array.isArray(v.care.records))) return false;
  if (
    v.league !== undefined &&
    (!isObject(v.league) ||
      typeof v.league.boutIndex !== 'number' ||
      !Array.isArray(v.league.hallOfFame))
  ) {
    return false;
  }
  if (v.rival !== undefined && v.rival !== null && !isObject(v.rival)) return false;
  if (v.overworld !== undefined && v.overworld !== null && !isObject(v.overworld)) return false;

  if (typeof v.worldSeed !== 'number') return false;
  if (typeof v.inGameDate !== 'string') return false;
  if (typeof v.lastPlayedAt !== 'number') return false;

  if (!isObject(v.settings)) return false;

  return true;
}
